import { isPurchaseOrderPaymentStatusFilter } from './paymentStatusDisplay'

/** Prepositions/articles kept lowercase in status labels (except when first). */
const STATUS_LABEL_SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'by',
  'for',
  'from',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
  'with',
])

/**
 * Convert enum-style status values into readable UI labels.
 * e.g. READY_FOR_DISPATCH → Ready for Dispatch
 * Keeps the original enum for API/filter values; display only.
 */
export const formatStatusLabel = (status) => {
  if (status == null || status === '') return status || ''
  const words = String(status).trim().split(/_+/).filter(Boolean)
  if (!words.length) return String(status)
  return words
    .map((word, index) => {
      const lower = word.toLowerCase()
      if (index > 0 && STATUS_LABEL_SMALL_WORDS.has(lower)) return lower
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

/** Admin Complete sets meta.locked — order-level status stays Completed, not Partially Delivered, except modify qty decrease. */
export const isPurchaseOrderAdminLocked = (order) =>
  !!(order?.locked || order?.meta?.locked)

/** Fully delivered current qty but grocer originally ordered more before admin decrease. */
export const matchesModifyDecreaseCompletedDelivery = (order) => {
  if (!order) return false
  const ordered = Number(order.total_ordered_units)
  const original = Number(order.total_original_ordered_units)
  const received = Number(order.total_received_units)
  const remaining = Number(order.total_remaining_units)
  if (!Number.isFinite(original) || original <= ordered) return false
  if (!Number.isFinite(ordered) || ordered <= 0) return false
  if (!Number.isFinite(received) || received <= 0) return false
  if (!Number.isFinite(remaining) || remaining !== 0) return false
  return received >= ordered
}

export const CLOSED_WITH_PARTIAL_DELIVERY_LABEL = 'Closed with Partial Delivery'

const completionRemovedUndelivered = (order) =>
  order?.meta?.completion_removed_undelivered ||
  order?.completion_removed_undelivered ||
  null

/**
 * Admin force-completed a short delivery: delivered (good) qty < ordered qty.
 * After Complete, line quantities are trimmed so totals alone cannot tell —
 * use the persisted flag / removed-undelivered meta.
 */
export const isClosedWithPartialDelivery = (order, isSales = false) => {
  if (!order || isSales) return false
  if (String(order.order_type || '').toLowerCase() !== 'purchase') return false
  if (!isPurchaseOrderAdminLocked(order)) return false
  if (order.closed_with_partial_delivery === true) return true
  if (order.meta?.closed_with_partial_delivery === true) return true
  const removed = completionRemovedUndelivered(order)
  if (removed) {
    const removedIds = removed.removed_line_ids || []
    const trimmed = removed.trimmed_lines || []
    if (removedIds.length > 0 || trimmed.length > 0) return true
  }
  const ordered = Number(order.total_ordered_units)
  const received = Number(order.total_received_units)
  return (
    Number.isFinite(ordered) &&
    ordered > 0 &&
    Number.isFinite(received) &&
    received < ordered
  )
}

/** True when Complete would close leftover (not-yet-received) quantity. */
export const wouldCompleteAsPartialDelivery = (order) => {
  if (!order) return false
  const remaining = Number(order.total_remaining_units)
  if (Number.isFinite(remaining) && remaining > 0) return true
  const remainingToShip = Number(order.total_remaining_to_ship_units)
  if (Number.isFinite(remainingToShip) && remainingToShip > 0) return true
  const ordered = Number(order.total_ordered_units)
  const received = Number(order.total_received_units)
  return (
    Number.isFinite(ordered) &&
    ordered > 0 &&
    Number.isFinite(received) &&
    received < ordered
  )
}

export const PARTIALLY_COMPLETED_LABEL = 'Partially Completed'

/** Admin modify decreased qty; order finished against reduced qty (final). */
export const shouldShowPartiallyCompleted = (order, isSales) => {
  if (!order || isSales) return false
  if (String(order.order_type || '').toLowerCase() !== 'purchase') return false
  return matchesModifyDecreaseCompletedDelivery(order)
}

/** Purchase list: some units delivered/received but lines still outstanding. */
export const shouldShowPartialReceived = (order, isSales) => {
  if (!order || isSales) return false
  if (String(order.order_type || '').toLowerCase() !== 'purchase') return false
  if (matchesModifyDecreaseCompletedDelivery(order)) return false
  if (isPurchaseOrderAdminLocked(order)) return false
  const status = String(order.order_status || '').toUpperCase()
  if (status !== 'COMPLETED' && status !== 'DISPATCHED') return false
  const ordered = Number(order.total_ordered_units)
  const received = Number(order.total_received_units)
  const remaining = Number(order.total_remaining_units)
  if (!Number.isFinite(ordered) || ordered <= 0) return false
  if (!Number.isFinite(received) || !Number.isFinite(remaining)) return false
  return received > 0 && remaining > 0
}

/** All units accounted with no good received (returns only). */
export const shouldShowFullyReturned = (order, isSales) => {
  if (!order || isSales) return false
  if (String(order.order_type || '').toLowerCase() !== 'purchase') return false
  const status = String(order.order_status || '').toUpperCase()
  if (status !== 'COMPLETED' && status !== 'DISPATCHED') return false
  const received = Number(order.total_received_units)
  const remaining = Number(order.total_remaining_units)
  if (!Number.isFinite(received) || !Number.isFinite(remaining)) return false
  if (remaining > 0 || received > 0) return false
  const shipmentReturned = (order.shipments || []).some(
    (s) =>
      String(s.status || '').toUpperCase() === 'FULLY_RETURNED' ||
      (s.items || []).some((i) => Number(i.damaged_quantity) > 0),
  )
  const receiptReturned = (order.receipt_items || []).some(
    (ri) => Number(ri.damaged_quantity) > 0,
  )
  const returnedUnits = Number(order.total_returned_units)
  return (
    shipmentReturned ||
    receiptReturned ||
    (Number.isFinite(returnedUnits) && returnedUnits > 0)
  )
}

export const getUserStatusLabel = (userStatus, isSales = false) => {
  if (!userStatus) return userStatus || ''

  const statusMap = {
    COMPLETED: 'Completed',
    RECEIVED: 'Completed with issues',
    RECEIVED_PENDING_VERIFICATION: 'Completed with issues',
    IN_TRANSIT: 'In transit',
    SHIPPED: 'In transit',
    DISPATCHED: 'Dispatched',
  }

  return statusMap[userStatus] || formatStatusLabel(userStatus)
}

export const getPurchaseListStatusLabel = (order, isSales) => {
  if (shouldShowFullyReturned(order, isSales)) return 'Returned'
  if (isClosedWithPartialDelivery(order, isSales)) {
    return CLOSED_WITH_PARTIAL_DELIVERY_LABEL
  }
  if (shouldShowPartiallyCompleted(order, isSales)) {
    return PARTIALLY_COMPLETED_LABEL
  }
  if (shouldShowPartialReceived(order, isSales)) return 'Partially Delivered'
  const orderStatus = String(order?.order_status || '').toUpperCase()
  const awaitingFulfillmentWebhook =
    orderStatus === 'PROCUREMENT_STARTED' &&
    (order?.shipments || []).some(
      (shipment) =>
        String(shipment?.status || '').toUpperCase() ===
        'FULFILLMENT_CENTER_ASSIGNED',
    )
  if (awaitingFulfillmentWebhook) return 'Fulfillment Center Assigned'
  return getUserStatusLabel(order.user_status || order.order_status, isSales)
}

export const getStatusColor = (status, userStatusLabel) => {
  if (userStatusLabel) {
    const normalizedLabel = userStatusLabel.toUpperCase()
    if (normalizedLabel === 'PARTIALLY DELIVERED' || normalizedLabel === 'PARTIAL RECEIVED') {
      return 'warning'
    }
    if (normalizedLabel === 'PARTIALLY COMPLETED') {
      return 'warning'
    }
    if (normalizedLabel === 'CLOSED WITH PARTIAL DELIVERY') {
      return 'warning'
    }
    if (normalizedLabel === 'RETURNED') {
      return 'default'
    }
    if (normalizedLabel === 'COMPLETED') {
      return 'success'
    }
    if (normalizedLabel.includes('COMPLETED WITH ISSUES') || normalizedLabel.includes('ISSUES')) {
      return 'warning'
    }
  }

  const statusColors = {
    CREATED: 'default',
    ACCEPTED: 'info',
    REJECTED: 'error',
    PROCUREMENT_STARTED: 'warning',
    FULFILLMENT_CENTER_ASSIGNED: 'warning',
    READY_FOR_DISPATCH: 'warning',
    DISPATCHED: 'info',
    DELIVERED: 'success',
    CANCELLED: 'error',
    FAILED: 'error',
    COMPLETED: 'success',
    RECEIVED_PENDING_VERIFICATION: 'warning',
    RESOLVED_WITH_CREDIT: 'success',
  }
  return statusColors[status] || 'default'
}

/**
 * Creation timestamp for the sales / purchase orders list date column.
 * Prefer API `order_date` (immutable create/sale instant; date_created equivalent).
 * Do not use `date_modified` here — list sorting stays on modified date server-side.
 */
export function getOrderListCreatedDate(order) {
  if (!order || typeof order !== 'object') return null
  return order.order_date ?? order.date_created ?? order.dateCreated ?? null
}

function parseDisplayDate(dateString) {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    return Number.isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

/** Format API order timestamps in the active browser timezone. */
export const formatOrderDate = (dateString) => {
  if (!dateString) return '-'
  const date = parseDisplayDate(dateString)
  if (!date) return String(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Date-only portion of an API timestamp in the active browser timezone. */
export const formatOrderDateOnly = (dateString) => {
  if (!dateString) return '-'
  const date = parseDisplayDate(dateString)
  if (!date) return String(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** Time-only portion of an API timestamp in the active browser timezone. */
export const formatOrderTimeOnly = (dateString) => {
  if (!dateString) return '-'
  const date = parseDisplayDate(dateString)
  if (!date) return '-'
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export const formatOrderAmount = (amount) => {
  if (typeof amount === 'number') {
    return `৳${amount.toFixed(2)}`
  }
  return `৳${parseFloat(amount || 0).toFixed(2)}`
}

/** Shipment statuses that mean the shipment has left the warehouse. */
export const DISPATCHED_SHIPMENT_STATUSES = new Set([
  'DISPATCHED',
  'PENDING_RECEIPT',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'FULLY_RETURNED',
])

/** Real dispatched shipments can carry proof of delivery. Legacy id 0 cannot. */
export function isDispatchedShipment(shipment) {
  if (!shipment || shipment.legacy) return false
  if (!(Number(shipment.id) > 0)) return false
  return DISPATCHED_SHIPMENT_STATUSES.has(String(shipment.status || '').toUpperCase())
}

/** Rows-per-page options on the orders list. Default is omitted from the URL. */
export const ORDERS_LIST_PAGE_SIZES = [5, 10, 25, 50]
export const ORDERS_LIST_DEFAULT_LIMIT = 10

/**
 * Status values offered by the purchase-order "Filter by Status" dropdown.
 * Unknown values fall back to no status filter.
 */
const PURCHASE_ORDER_STATUS_FILTERS = new Set([
  'CREATED',
  'MODIFIED',
  'ACCEPTED',
  'REJECTED',
  'PROCUREMENT_STARTED',
  'FULFILLMENT_CENTER_ASSIGNED',
  'READY_FOR_DISPATCH',
  'DISPATCHED',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'CLOSED_WITH_PARTIAL_DELIVERY',
  'RETURN_IN_PROGRESS',
  'RETURN_REJECTED',
  'RETURN_ACCEPTED',
  'RETURN_ACCEPTED_WITH_RECONCILIATIONS',
  'DELIVERED',
  'CANCELLED',
  'FAILED',
])

/**
 * Read orders-list URL state.
 * `page` in the URL is 1-based; the returned page index is 0-based.
 * Sales orders ignore `status` (that filter is purchase-only).
 * `orgIds` is a separate shop filter (comma-separated shop ids).
 */
export function parseOrdersListSearchParams(searchParams, { includeStatus = true } = {}) {
  const q = searchParams.get('q') || ''
  const page = Math.max(0, (parseInt(searchParams.get('page') || '1', 10) || 1) - 1)
  const parsedLimit = parseInt(searchParams.get('limit') || String(ORDERS_LIST_DEFAULT_LIMIT), 10)
  const limit = ORDERS_LIST_PAGE_SIZES.includes(parsedLimit) ? parsedLimit : ORDERS_LIST_DEFAULT_LIMIT
  let status = ''
  let paymentStatus = ''
  if (includeStatus) {
    const raw = String(searchParams.get('status') || '').trim()
    status = PURCHASE_ORDER_STATUS_FILTERS.has(raw) ? raw : ''
    const rawPayment = String(searchParams.get('payment_status') || '').trim()
    paymentStatus = isPurchaseOrderPaymentStatusFilter(rawPayment)
      ? rawPayment
      : ''
    if (!paymentStatus && isPurchaseOrderPaymentStatusFilter(raw)) {
      paymentStatus = raw
    }
  }
  const orgIds = String(searchParams.get('orgIds') || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
  return { q, status, paymentStatus, page, limit, orgIds }
}

/** Build the orders-list query. Empty search, empty status, page 1, and the default limit are omitted. */
export function buildOrdersListSearchParams({
  q,
  status,
  paymentStatus,
  page,
  limit,
  orgIds,
}) {
  const params = new URLSearchParams()
  const trimmed = (q || '').trim()
  if (trimmed) params.set('q', trimmed)
  if (status) params.set('status', status)
  if (paymentStatus) params.set('payment_status', paymentStatus)
  const ids = (orgIds || []).map((id) => String(id).trim()).filter(Boolean)
  if (ids.length > 0) params.set('orgIds', ids.join(','))
  if (page > 0) params.set('page', String(page + 1))
  if (limit !== ORDERS_LIST_DEFAULT_LIMIT) params.set('limit', String(limit))
  return params
}

export function ordersListHasExplicitParams(searchParams) {
  return (
    searchParams.has('q') ||
    searchParams.has('status') ||
    searchParams.has('payment_status') ||
    searchParams.has('orgIds') ||
    searchParams.has('page') ||
    searchParams.has('limit')
  )
}

/**
 * Map UI list filters → GET orders query params.
 * `page` in filters is 0-based; API page is 1-based.
 */
export function toOrdersApiParams(
  { q, status, paymentStatus, page, limit, orgIds },
  { isSales = false } = {},
) {
  const params = {
    page: (Number.isFinite(page) ? page : 0) + 1,
    limit: limit ?? ORDERS_LIST_DEFAULT_LIMIT,
    orderType: isSales ? 'Sale' : 'Purchase',
  }
  if (!isSales && status) {
    params.status = status
  }
  if (!isSales && paymentStatus) {
    params.payment_status = paymentStatus
  }
  const trimmed = (q || '').trim()
  if (trimmed) {
    params.search = trimmed
  }
  const ids = (orgIds || []).map((id) => String(id).trim()).filter(Boolean)
  if (ids.length > 0) {
    params.orgIds = ids.join(',')
  }
  return params
}

/** Full list URL (path + current query) used when returning from order detail. */
export function getOrdersListReturnPath(pathname, searchParams) {
  const base = String(pathname || '').startsWith('/orders/sales') ? '/orders/sales' : '/orders/purchase'
  const query = typeof searchParams?.toString === 'function' ? searchParams.toString() : ''
  return query ? `${base}?${query}` : base
}

export function buildOrderDetailPath(id, returnPath) {
  const params = new URLSearchParams()
  if (returnPath) params.set('return', returnPath)
  const query = params.toString()
  return query ? `/orders/${id}?${query}` : `/orders/${id}`
}

export function resolveOrderDetailReturnPath(searchParams, locationState, fallbackPath) {
  const fromQuery = searchParams?.get?.('return')
  if (fromQuery && fromQuery.startsWith('/')) return fromQuery
  if (locationState?.ordersListPath && String(locationState.ordersListPath).startsWith('/')) {
    return locationState.ordersListPath
  }
  return fallbackPath || '/orders/purchase'
}
